import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { MatchScoreBadge } from '@/components/ui/MatchScoreBadge'
import { StatusPill } from '@/components/ui/StatusPill'
import { Tabs } from '@/components/ui/Tabs'
import { AiShortlistPanel } from '@/features/recruiter/components/AiShortlistPanel'
import { ApplicantStatusActions } from '@/features/recruiter/components/ApplicantStatusActions'
import { CandidateDetailDrawer } from '@/features/recruiter/components/CandidateDetailDrawer'
import type { Applicant } from '@/features/recruiter/api/applicants.api'
import type { RecruiterJob } from '@/features/recruiter/api/jobs.api'
import { useApplicantsQuery } from '@/features/recruiter/hooks/useApplicants'
import { useRecruiterJobQuery } from '@/features/recruiter/hooks/useJobs'
import { formatDate, formatMonthsAsYears } from '@/utils/format'

// Temporarily hidden per request — flip back to true to restore the AI
// Score column and the "Run AI Shortlist" button.
const SHOW_AI_FEATURES = false

interface MatchLine {
  label: string
  matched: boolean
  detail: string
}

interface JobMatchResult {
  score: number
  lines: MatchLine[]
  matchedSkills: string[]
}

/**
 * Client-side rule-based match score: compares the applicant's known profile
 * fields against the job's requirements. No backend call, no AI.
 *
 * Weights:
 *   Skills     50% — overlap between candidate's topSkills and job's skillsRequired
 *   Experience 30% — whether totalExperienceMonths falls within job's min/max range
 *   Location   20% — whether currentCity or currentState matches job's location
 *
 * When a job has no skills/experience/location requirements, that signal is
 * excluded and the remaining weights are scaled up proportionally.
 */
function computeJobMatch(job: RecruiterJob, applicant: Applicant): JobMatchResult {
  const lines: MatchLine[] = []
  const matchedSkills: string[] = []

  type Signal = { weight: number; score: number }
  const signals: Signal[] = []

  // --- Skills (weight 50) ---
  if (job.skillsRequired.length > 0) {
    const wanted = job.skillsRequired.map((s) => s.toLowerCase())
    const matched = applicant.candidate.skills
      .map((s) => s.name)
      .filter((s) => wanted.includes(s.toLowerCase()))
    matchedSkills.push(...matched)
    const skillScore = Math.min(matched.length / wanted.length, 1)
    signals.push({ weight: 50, score: skillScore })
    lines.push({
      label: 'Skills',
      matched: matched.length > 0,
      detail: matched.length > 0 ? matched.join(', ') : `None of: ${job.skillsRequired.slice(0, 3).join(', ')}`,
    })
  }

  // --- Experience (weight 30) ---
  const hasExpReq = job.experienceMinMonths != null || job.experienceMaxMonths != null
  if (hasExpReq) {
    const candidateMonths = applicant.candidate.isFresher ? 0 : (applicant.candidate.totalExperienceMonths ?? 0)
    const min = job.experienceMinMonths ?? 0
    const max = job.experienceMaxMonths ?? Number.POSITIVE_INFINITY
    const inRange = candidateMonths >= min && candidateMonths <= max
    const expScore = inRange ? 1 : 0
    signals.push({ weight: 30, score: expScore })
    const candidateLabel = applicant.candidate.isFresher ? 'Fresher' : formatMonthsAsYears(candidateMonths)
    const rangeLabel =
      job.experienceMinMonths != null && job.experienceMaxMonths != null
        ? `${formatMonthsAsYears(job.experienceMinMonths)}–${formatMonthsAsYears(job.experienceMaxMonths)}`
        : job.experienceMinMonths != null
          ? `${formatMonthsAsYears(job.experienceMinMonths)}+`
          : `Up to ${formatMonthsAsYears(job.experienceMaxMonths ?? 0)}`
    lines.push({ label: 'Experience', matched: inRange, detail: `${candidateLabel} (required: ${rangeLabel})` })
  }

  // --- Location (weight 20) ---
  if (job.locationCity || job.locationState) {
    const jobLoc = [job.locationCity, job.locationState].filter(Boolean).map((l) => l!.toLowerCase())
    const candidateLocs = [
      applicant.candidate.currentCity,
      applicant.candidate.currentState,
    ].filter(Boolean).map((l) => l!.toLowerCase())
    const matched = jobLoc.some((jl) => candidateLocs.includes(jl))
    signals.push({ weight: 20, score: matched ? 1 : 0 })
    const candidateLocLabel = [applicant.candidate.currentCity, applicant.candidate.currentState].filter(Boolean).join(', ') || '—'
    const jobLocLabel = [job.locationCity, job.locationState].filter(Boolean).join(', ')
    lines.push({ label: 'Location', matched, detail: `${candidateLocLabel} (job: ${jobLocLabel})` })
  }

  if (signals.length === 0) return { score: 0, lines: [], matchedSkills: [] }

  const totalWeight = signals.reduce((s, sig) => s + sig.weight, 0)
  const weightedSum = signals.reduce((s, sig) => s + sig.score * sig.weight, 0)
  const score = Math.round((weightedSum / totalWeight) * 100)

  return { score, lines, matchedSkills }
}

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'applied', label: 'Applied' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'shortlisted', label: 'Shortlisted' },
  { key: 'hired', label: 'Hired' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'withdrawn', label: 'Withdrawn' },
]

function MatchCell({ job, applicant }: { job: RecruiterJob; applicant: Applicant }) {
  const [hovered, setHovered] = useState(false)
  const { score, lines } = computeJobMatch(job, applicant)

  return (
    <div
      className="position-relative d-inline-block"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <MatchScoreBadge score={score} />
      {hovered && lines.length > 0 && (
        <div
          className="card shadow-lg p-2"
          style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, minWidth: 260, marginTop: 6 }}
        >
          <p className="fw-semibold mb-1 fs-12">Match breakdown</p>
          {lines.map((line) => (
            <p key={line.label} className="mb-0 fs-12">
              <span className={line.matched ? 'text-success' : 'text-danger'}>{line.matched ? '✓' : '✗'}</span>{' '}
              <span className="text-muted">{line.label}:</span> {line.detail}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}

export function ApplicantsPage() {
  const params = useParams<{ jobId: string }>()
  const jobId = Number(params.jobId)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Applicant | null>(null)

  const { data: job } = useRecruiterJobQuery(jobId)
  const { data, isPending } = useApplicantsQuery(jobId, { status: status || undefined, page })

  const sortedApplicants = data?.applicants
    ? job
      ? [...data.applicants].sort(
          (a, b) => computeJobMatch(job, b).score - computeJobMatch(job, a).score,
        )
      : data.applicants
    : []

  const columns: DataTableColumn<Applicant>[] = [
    {
      key: 'name',
      header: 'Candidate',
      render: (row) => (
        <button type="button" className="btn btn-link p-0" onClick={() => setSelected(row)}>
          {row.candidate.name}
        </button>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (row) =>
        [row.candidate.currentCity, row.candidate.currentState].filter(Boolean).join(', ') || '—',
    },
    {
      key: 'experience',
      header: 'Experience',
      render: (row) =>
        row.candidate.isFresher ? 'Fresher' : formatMonthsAsYears(row.candidate.totalExperienceMonths),
    },
    {
      key: 'jobMatch',
      header: 'Job Match',
      render: (row) => job ? <MatchCell job={job} applicant={row} /> : '—',
    },
    { key: 'status', header: 'Status', render: (row) => <StatusPill status={row.status} /> },
    ...(SHOW_AI_FEATURES
      ? [
          {
            key: 'aiMatchScore',
            header: 'AI Score',
            render: (row: Applicant) => {
              // ai_match_score is a Laravel decimal-cast column, which
              // serializes as a JSON string ("85.00"), not a number —
              // coerce before using it.
              const score = row.aiMatchScore == null ? null : Number(row.aiMatchScore)
              return score == null ? '—' : <MatchScoreBadge score={score} />
            },
          } satisfies DataTableColumn<Applicant>,
        ]
      : []),
    { key: 'appliedAt', header: 'Applied', render: (row) => formatDate(row.appliedAt) },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <ApplicantStatusActions jobId={jobId} applicationId={row.applicationId} status={row.status} />
      ),
    },
  ]

  return (
    <>
      <div className="my-4 page-header-breadcrumb d-flex align-items-start justify-content-between flex-wrap gap-2">
        <div>
          <p className="fw-semibold fs-20 mb-0">Applicants{job ? ` — ${job.title}` : ''}</p>
        </div>
        {SHOW_AI_FEATURES && <AiShortlistPanel jobId={jobId} />}
      </div>

      <Tabs
        items={STATUS_TABS}
        activeKey={status}
        onChange={(key) => {
          setStatus(key)
          setPage(1)
        }}
      />

      <div className="card custom-card">
        <DataTable
          columns={columns}
          rows={sortedApplicants}
          rowKey={(row) => row.applicationId}
          loading={isPending}
          emptyMessage="No applicants yet."
          pagination={data?.meta}
          onPageChange={setPage}
        />
      </div>

      <CandidateDetailDrawer
        show={selected != null}
        onClose={() => setSelected(null)}
        candidate={selected?.candidate}
        footer={
          selected && (
            <ApplicantStatusActions
              jobId={jobId}
              applicationId={selected.applicationId}
              status={selected.status}
              showDocuments
            />
          )
        }
      />
    </>
  )
}
